import axios from 'axios';

// Types for the DTOs
export interface DiseaseCategoryDto {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  diseasesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiseaseCategoryDto {
  name: string;
  description?: string;
  isActive: boolean;
}

export interface UpdateDiseaseCategoryDto {
  name: string;
  description?: string;
  isActive: boolean;
}

export interface DiseaseCustomFieldDto {
  id: number;
  diseaseId: number;
  fieldName: string;
  fieldType: number;
  fieldTypeName: string;
  isRequired: boolean;
  selectOptions?: string[];
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiseaseCustomFieldDto {
  fieldName: string;
  fieldType: number;
  isRequired: boolean;
  selectOptions?: string[];
  order: number;
}

export interface UpdateDiseaseCustomFieldDto {
  id?: number;
  fieldName: string;
  fieldType: number;
  isRequired: boolean;
  selectOptions?: string[];
  order: number;
}

export interface DiseaseDto {
  id: number;
  name: string;
  categoryId: number;
  categoryName: string;
  type: number;
  typeName: string;
  description?: string;
  isActive: boolean;
  customFields: DiseaseCustomFieldDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiseaseDto {
  name: string;
  categoryId: number;
  type: number;
  description?: string;
  isActive: boolean;
  customFields: CreateDiseaseCustomFieldDto[];
}

export interface UpdateDiseaseDto {
  name: string;
  categoryId: number;
  type: number;
  description?: string;
  isActive: boolean;
  customFields: UpdateDiseaseCustomFieldDto[];
}

export interface StatusDto {
  id: number;
  name: string;
  description?: string;
  color: string;
  group: number;
  groupName: string;
  statusType: number;
  statusTypeName: string;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  nextStatusesDetails: StatusDto[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
  isSystemDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStatusDto {
  name: string;
  description?: string;
  color: string;
  group: number;
  statusType: number;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
}

export interface UpdateStatusDto {
  name: string;
  description?: string;
  color: string;
  group: number;
  statusType: number;
  isDefault: boolean;
  countInPipeline: boolean;
  countAsConversion: boolean;
  sortOrder: number;
  nextStatuses: number[];
  autoAlertDays?: number;
  alertContent?: string;
  createReminder: boolean;
  isActive: boolean;
}

export interface LeadSourceDto {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  leadsCount: number;
  successRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadSourceDto {
  name: string;
  description?: string;
  isActive: boolean;
}

export interface UpdateLeadSourceDto {
  name: string;
  description?: string;
  isActive: boolean;
}


// Disease Categories API
export const diseaseCategoriesApi = {
  getAll: async (): Promise<DiseaseCategoryDto[]> => {
    const response = await axios.get('/settings/disease-categories');
    return response.data;
  },

  getById: async (id: number): Promise<DiseaseCategoryDto> => {
    const response = await axios.get(`/settings/disease-categories/${id}`);
    return response.data;
  },

  create: async (data: CreateDiseaseCategoryDto): Promise<DiseaseCategoryDto> => {
    const response = await axios.post('/settings/disease-categories', data);
    return response.data;
  },

  update: async (id: number, data: UpdateDiseaseCategoryDto): Promise<DiseaseCategoryDto> => {
    const response = await axios.put(`/settings/disease-categories/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`/settings/disease-categories/${id}`);
  },

  search: async (searchTerm: string): Promise<DiseaseCategoryDto[]> => {
    const response = await axios.get(`/settings/disease-categories/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  }
};

// Diseases API
export const diseasesApi = {
  getAll: async (): Promise<DiseaseDto[]> => {
    const response = await axios.get('/settings/diseases');
    return response.data;
  },

  getById: async (id: number): Promise<DiseaseDto> => {
    const response = await axios.get(`/settings/diseases/${id}`);
    return response.data;
  },

  create: async (data: CreateDiseaseDto): Promise<DiseaseDto> => {
    const response = await axios.post('/settings/diseases', data);
    return response.data;
  },

  update: async (id: number, data: UpdateDiseaseDto): Promise<DiseaseDto> => {
    const response = await axios.put(`/settings/diseases/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`/settings/diseases/${id}`);
  },

  search: async (searchTerm: string, categoryId?: number, type?: number): Promise<DiseaseDto[]> => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('q', searchTerm);
    if (categoryId) params.append('categoryId', categoryId.toString());
    if (type) params.append('type', type.toString());
    
    const response = await axios.get(`/settings/diseases/search?${params.toString()}`);
    return response.data;
  }
};

// Statuses API
export const statusesApi = {
  getAll: async (): Promise<StatusDto[]> => {
    const response = await axios.get('/settings/statuses');
    return response.data;
  },

  getById: async (id: number): Promise<StatusDto> => {
    const response = await axios.get(`/settings/statuses/${id}`);
    return response.data;
  },

  create: async (data: CreateStatusDto): Promise<StatusDto> => {
    const response = await axios.post('/settings/statuses', data);
    return response.data;
  },

  update: async (id: number, data: UpdateStatusDto): Promise<StatusDto> => {
    const response = await axios.put(`/settings/statuses/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`/settings/statuses/${id}`);
  },

  search: async (searchTerm: string, group?: number): Promise<StatusDto[]> => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('q', searchTerm);
    if (group) params.append('group', group.toString());
    
    const response = await axios.get(`/settings/statuses/search?${params.toString()}`);
    return response.data;
  }
};

// Lead Sources API
export const leadSourcesApi = {
  getAll: async (): Promise<LeadSourceDto[]> => {
    const response = await axios.get('/settings/lead-sources');
    return response.data;
  },

  getById: async (id: number): Promise<LeadSourceDto> => {
    const response = await axios.get(`/settings/lead-sources/${id}`);
    return response.data;
  },

  create: async (data: CreateLeadSourceDto): Promise<LeadSourceDto> => {
    const response = await axios.post('/settings/lead-sources', data);
    return response.data;
  },

  update: async (id: number, data: UpdateLeadSourceDto): Promise<LeadSourceDto> => {
    const response = await axios.put(`/settings/lead-sources/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`/settings/lead-sources/${id}`);
  },

  search: async (searchTerm: string): Promise<LeadSourceDto[]> => {
    const response = await axios.get(`/settings/lead-sources/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  }
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';